import sortBy from 'lodash/sortBy';

import { InteractiveResponseItem } from '../settings/interactive-response.constants';
import { shuffleArray } from '../../../shared/interactive-drag-drop/interactive-drag-drop.utils';
import {
  DragItem,
  DropItem,
} from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.constants';
import { isEverythingCorrect } from '../../shared/interactive-response-shared.utils';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import { InquisitiveActivitiesResponseVariant } from 'global/constants';

export function populateAnswers(
  attemptedAnswers: InteractiveResponseAnswerFragment,
  items: InteractiveResponseItem[],
  variant: InquisitiveActivitiesResponseVariant
) {
  const sortedItems = sortBy(items, 'sort');
  const dropItems = sortedItems.map((item, index) => {
    const answer = attemptedAnswers.interactiveResponseItems.find(
      answer => answer.dropZoneId === item.itemId
    );
    return {
      id: item.itemId,
      index,
      label: `${item?.answerLabel || index + 1}`,
      zones: [
        {
          dragItemId: answer?.imageId || null,
          previewItemId: null,
          isCorrect: answer?.isCorrect || false,
        },
      ],
    };
  });

  const dragItems: DragItem[] = sortedItems.map((item, index) => ({
    id: item.itemId,
    value: item.itemId,
    index,
    isDropped: true,
    question: {
      label: item.questionLabel || '1',
      variant: item.variant || variant,
      baseColour: item.baseColour,
      text: item.text,
      richtext: item.richtext,
      audioUrl: item.audio?.original || null,
      image: {
        itemId: item.itemId,
        small: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
        large: item?.imageUrls?.[1]?.urls?.[0]?.url || null,
        description: item.image?.description || '',

        // existing content in prod will have imageMode = null
        isFit: item.imageMode === null ? false : !item.cropData,
      },
    },
  }));

  return {
    dragItems,
    dropItems,
  };
}

export function getAnswers(
  attemptedAnswers: InteractiveResponseAnswerFragment,
  items: InteractiveResponseItem[],
  variant: InquisitiveActivitiesResponseVariant
) {
  // sort the drag items
  const sortedItems = sortBy(items, 'sort');

  // "imageId" is legacy but now represents the "itemId" it was originally tied to
  // "dropZoneId" is the zone it was placed in
  return sortedItems.map((item, index) => {
    const submittedAnswer = attemptedAnswers.interactiveResponseItems.find(
      attemptedAnswer => attemptedAnswer.dropZoneId === item.itemId
    );

    const selectedItem = sortedItems.find(
      sortedItem => sortedItem.itemId === submittedAnswer?.imageId
    );

    return {
      id: item?.itemId,
      index,
      label: `${item.answerLabel || index + 1}`,
      isCorrect: submittedAnswer?.isCorrect || false,
      image: selectedItem?.imageUrls?.[0]?.urls?.[0]?.url,
      isFit: selectedItem?.imageMode === null ? false : !item?.cropData,
      description: selectedItem?.image?.description || '',
      variant: selectedItem?.variant || variant,
      baseColour: selectedItem?.baseColour || null,
      text: selectedItem?.text || null,
      richtext: selectedItem?.richtext || null,
      audioUrl: selectedItem?.audio?.original || null,
    };
  });
}

export function initialise(
  variant: InquisitiveActivitiesResponseVariant,
  items: InteractiveResponseItem[],
  shuffle: boolean | undefined = true
) {
  const sortedItems = sortBy(items, 'sort');
  const dropItems = sortedItems.map((item, index) => ({
    id: item.itemId,
    index,
    label: `${item.answerLabel || index + 1}`,
    zones: [
      {
        dragItemId: null,
        previewItemId: null,
      },
    ],
  }));

  const shuffled = shuffle ? shuffleArray([...sortedItems]) : sortedItems;
  const dragItems: DragItem[] = shuffled.map((item, index) => ({
    id: item.itemId,
    value: item.itemId,
    index,
    isDropped: false,
    question: {
      label: item.questionLabel || '1',
      variant: item.variant || variant,
      baseColour: item.baseColour,
      text: item.text,
      richtext: item.richtext,
      audioUrl: item.audio?.original || null,
      image: {
        itemId: item.itemId,
        small: item?.imageUrls?.[0]?.urls?.[0]?.url || null,
        large: item?.imageUrls?.[1]?.urls?.[0]?.url || null,
        description: item.image?.description || '',

        // existing content in prod will have imageMode = null
        isFit: item.imageMode === null ? false : !item.cropData,
      },
    },
  }));

  return {
    dragItems,
    dropItems,
  };
}

export function getResults(dragItems: DragItem[], dropItems: DropItem[]) {
  return isEverythingCorrect(dropItems, dragItems)
    ? `Good job! 🎉`
    : `Don't give up!`;
}

export function resetIncorrectItems(
  currentDragItems: DragItem[],
  currentDropItems: DropItem[]
) {
  const updatedDragItems = [...currentDragItems];
  const updatedDropItems = [...currentDropItems];

  // Find incorrectly placed items and clear isCorrect for all zones
  currentDropItems.forEach((dropItem, dropIndex) => {
    const zone = dropItem.zones[0];

    // If the item is incorrect, reset it
    if (zone.isCorrect === false && zone.dragItemId) {
      // Find the drag item that was incorrectly placed
      const dragItemIndex = updatedDragItems.findIndex(
        drag => drag.id === zone.dragItemId
      );

      if (dragItemIndex !== -1) {
        // Reset the drag item to not dropped
        updatedDragItems[dragItemIndex] = {
          ...updatedDragItems[dragItemIndex],
          isDropped: false,
        };
      }

      // Clear the drop zone (remove incorrect item)
      updatedDropItems[dropIndex] = {
        ...updatedDropItems[dropIndex],
        zones: [
          {
            dragItemId: null,
            previewItemId: null,
            isCorrect: undefined,
          },
        ],
      };
    } else {
      // For correct items, clear isCorrect to reset the checked state
      updatedDropItems[dropIndex] = {
        ...updatedDropItems[dropIndex],
        zones: [
          {
            ...zone,
            isCorrect: undefined,
          },
        ],
      };
    }
  });

  return {
    dragItems: updatedDragItems,
    dropItems: updatedDropItems,
  };
}
